// src/components/EmailServiceModal.jsx
import React, { useState } from 'react';
import { X, Mail, Chrome, Globe, Shield, CheckCircle } from 'lucide-react';

const EmailServiceModal = ({ isOpen, onClose, onSelectService, emailData }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  if (!isOpen) return null;

  const emailServices = [
    {
      id: 'gmail',
      name: 'Gmail',
      icon: '📧',
      description: 'Connectez votre compte Gmail pour envoyer les emails',
      color: 'from-red-500 to-pink-600',
      provider: 'Google'
    },
    {
      id: 'outlook',
      name: 'Outlook',
      icon: '📨',
      description: 'Utilisez votre compte Microsoft Outlook',
      color: 'from-blue-500 to-cyan-600',
      provider: 'Microsoft'
    },
    {
      id: 'yahoo',
      name: 'Yahoo Mail',
      icon: '📮',
      description: 'Connectez votre compte Yahoo Mail',
      color: 'from-purple-500 to-indigo-600',
      provider: 'Yahoo'
    }
  ];

  const handleConnect = async (service) => {
    setSelectedService(service.id);
    setIsConnecting(true);

    try {
      // Simulation de la connexion OAuth
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Ici vous intégreriez la vraie logique OAuth
      onSelectService(service, emailData);
      
    } catch (error) {
      console.error('Erreur de connexion:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl mx-4 bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">🔗 Connexion Service Email</h3>
              <p className="text-gray-400 text-sm">
                Choisissez votre service email pour envoyer {emailData?.selectedCandidates?.length || 0} mail(s)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Services */}
        <div className="p-6">
          <div className="grid gap-4">
            {emailServices.map((service) => (
              <div
                key={service.id}
                className={`relative p-6 rounded-xl border transition-all duration-300 cursor-pointer ${
                  selectedService === service.id && isConnecting
                    ? 'bg-white/10 border-white/30 scale-105'
                    : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'
                }`}
                onClick={() => !isConnecting && handleConnect(service)}
              >
                {selectedService === service.id && isConnecting && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl border border-blue-500/30 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-blue-300 font-medium">Connexion en cours...</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center text-2xl shadow-lg`}>
                    {service.icon}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-bold text-white">{service.name}</h4>
                      <span className="px-2 py-1 bg-white/10 rounded-full text-gray-300 text-xs">
                        {service.provider}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3">{service.description}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Shield className="w-3 h-3" />
                        <span>Sécurisé OAuth 2.0</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>Connexion temporaire</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                      <Chrome className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info sécurité */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h6 className="text-blue-300 font-medium mb-1">🔒 Sécurité et Confidentialité</h6>
                <ul className="text-blue-200 text-sm space-y-1">
                  <li>• Connexion sécurisée via OAuth 2.0</li>
                  <li>• Aucun mot de passe stocké</li>
                  <li>• Accès temporaire uniquement pour l'envoi</li>
                  <li>• Déconnexion automatique après envoi</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-gray-400 text-sm">
            La connexion sera fermée automatiquement après l'envoi
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white transition-all"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailServiceModal;